import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import type { ParteAr, SegnoAr, SuperficieAr } from '../../types/ApparecchiArTypes';

/**
 * Il tavolo di mappatura: una fotografia e i richiami alle sue parti.
 *
 * ⚠️ **È un esploso da manuale di servizio, e si disegna come tale.** Il palloncino
 * numerato collegato al punto da una linea sottile non è decorazione: è la convenzione
 * che chi ripara apparecchi legge da sempre, e permette di indicare un connettore
 * piccolo senza coprirlo con l'etichetta che lo nomina. Un cerchio grande e opaco sopra
 * il pezzo lo nasconderebbe proprio mentre lo si indica.
 *
 * ⚠️ **Quattro origini, quattro colori, e non è estetica.** Un punto proposto da un
 * modello e mai confermato NON è un punto verificato: mescolarlo con quelli posati a
 * mano renderebbe impossibile sapere quali rileggere, e su un apparecchio in corrente
 * continua un'indicazione sbagliata è peggio di nessuna indicazione. Il tratteggio e il
 * viola dicono «questo non l'ha guardato nessuno».
 *
 * ⚠️ Le coordinate sono in pixel della FOTOGRAFIA, mai dello schermo: la stessa
 * annotazione deve valere su un telefono di qualunque dimensione. La conversione
 * avviene qui, in un posto solo.
 */

/**
 * Quanto è grande, di partenza, il cerchio che marca una parte — in frazione della
 * larghezza della fotografia.
 *
 * ⚠️ **Piccolo, perché deve INDICARE, non coprire.** Al 3,5% su uno scatto da dodici
 * megapixel il cerchio misurava un centinaio di pixel e nascondeva il connettore che
 * doveva mostrare. Questo valore finisce nei dati e viene ridisegnato sull'apparecchio
 * vero in cantiere: è una misura reale, non un dettaglio di schermata.
 *
 * ⚠️ Si REGOLA per punto (maiuscolo + rotella): un sezionatore in continua e una porta
 * di comunicazione non hanno la stessa taglia, e un cerchio uguale per tutti mente su
 * entrambi.
 */
export const RAGGIO_PREDEFINITO = 0.014;

export const COLORI = {
  posato: '#16A34A',
  serigrafia: '#38BDF8',
  bozza: '#A855F7',
  attivo: '#F59E0B',
  tracciato: '#E8EAED',
  tavolo: '#15181C',
} as const;

export function coloreDi(origine: SegnoAr['origine']): string {
  switch (origine) {
    case 'bozza_ia':
      return COLORI.bozza;
    case 'serigrafia':
      return COLORI.serigrafia;
    default:
      return COLORI.posato;
  }
}

interface Props {
  superficie: SuperficieAr;
  parti: ParteAr[];
  parteScelta: string | null;
  /**
   * Se il tavolo è ARMATO per posare.
   *
   * ⚠️ **Selezionare una parte non basta a poter posare.** Prima bastava, e il risultato
   * era che ogni clic sulla fotografia — per guardare, per orientarsi, per sbaglio —
   * spostava il punto della parte selezionata. Nessun avviso, nessun modo di accorgersene
   * se non ricontrollando. Armare è un gesto in più che si fa una volta e toglie una
   * classe intera di errori.
   */
  armato: boolean;
  onPosata: () => void;
  onPoni: (x: number, y: number) => void;
  onSposta: (parte: string, x: number, y: number) => void;
  onScegli: (parte: string) => void;
  onRaggio: (parte: string, r: number) => void;
}

export default function TavoloDiMappatura({
  superficie,
  parti,
  parteScelta,
  armato,
  onPosata,
  onPoni,
  onSposta,
  onScegli,
  onRaggio,
}: Props) {
  const contenitore = useRef<HTMLDivElement | null>(null);
  const [riquadro, setRiquadro] = useState({ x: 0, y: 0, w: 1, h: 1 });
  const [inTrascinamento, setInTrascinamento] = useState<string | null>(null);
  /*
    ⚠️ **Lo zoom serve alla precisione, non alla comodità.** Un connettore MC4 su uno
    scatto da dodici megapixel ridotto a schermo occupa una dozzina di pixel: posarci
    sopra un punto a occhio significa sbagliare di centimetri sull'apparecchio vero. Con
    l'ingrandimento si posa dove si vede, e il conto verso i pixel della fotografia
    resta identico — cambia solo quanto se ne vede per volta.
  */
  /*
    ⚠️ **La fotografia vecchia non deve restare a schermo mentre arriva la nuova.**
    Uno scatto da dodici megapixel pesa otto megabyte: cambiando faccia il browser ci
    mette secondi, e in quei secondi si vede il fianco destro mentre l'etichetta dice
    «fianco sinistro». Chi posa un punto in quel momento lo posa sulla faccia sbagliata,
    e nessuno se ne accorge.
  */
  const [caricata, setCaricata] = useState(false);
  useEffect(() => setCaricata(false), [superficie.immagine_url]);

  const [zoom, setZoom] = useState(1);
  const [scorrimento, setScorrimento] = useState({ x: 0, y: 0 });
  useEffect(() => {
    setZoom(1);
    setScorrimento({ x: 0, y: 0 });
  }, [superficie.id]);
  const premuto = useRef<{ x: number; y: number; scorrimento: { x: number; y: number } } | null>(null);
  const haScorso = useRef(false);

  /*
    ⚠️ L'immagine è in `object-fit: contain`, quindi lascia bande vuote ai lati o sopra:
    il rettangolo DISEGNATO non coincide con il contenitore. Senza questo calcolo ogni
    punto risulta spostato di quanto misurano le bande — tanto più quanto più le
    proporzioni differiscono, e in modo che sembra un errore di mira invece che di conto.
  */
  const misura = useCallback(() => {
    const el = contenitore.current;
    if (!el) return;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    const iw = superficie.larghezza_pixel || 1;
    const ih = superficie.altezza_pixel || 1;
    const scala = Math.min(cw / iw, ch / ih);
    const w = iw * scala;
    const h = ih * scala;
    setRiquadro({ x: (cw - w) / 2, y: (ch - h) / 2, w, h });
  }, [superficie.larghezza_pixel, superficie.altezza_pixel]);

  useEffect(() => {
    misura();
    const os = new ResizeObserver(misura);
    if (contenitore.current) os.observe(contenitore.current);
    return () => os.disconnect();
  }, [misura]);

  /*
    Il rettangolo davvero disegnato: la vestizione «contain» ingrandita attorno al
    proprio centro, poi spostata. Tutte le conversioni passano di qui, in un posto solo:
    è la ragione per cui aggiungere lo zoom non ha richiesto di ritoccare né la posa dei
    punti né il disegno dei richiami.
  */
  const vista = useMemo(() => {
    const w = riquadro.w * zoom;
    const h = riquadro.h * zoom;
    return {
      x: riquadro.x - (w - riquadro.w) / 2 + scorrimento.x,
      y: riquadro.y - (h - riquadro.h) / 2 + scorrimento.y,
      w,
      h,
    };
  }, [riquadro, zoom, scorrimento]);

  const versoImmagine = useCallback(
    (clientX: number, clientY: number) => {
      const el = contenitore.current;
      if (!el) return null;
      const r = el.getBoundingClientRect();
      const px = ((clientX - r.left - vista.x) / vista.w) * superficie.larghezza_pixel;
      const py = ((clientY - r.top - vista.y) / vista.h) * superficie.altezza_pixel;
      if (px < 0 || py < 0 || px > superficie.larghezza_pixel || py > superficie.altezza_pixel) {
        return null;
      }
      return { x: Math.round(px), y: Math.round(py) };
    },
    [vista, superficie.larghezza_pixel, superficie.altezza_pixel],
  );

  const versoSchermo = useCallback(
    (x: number, y: number) => ({
      sx: vista.x + (x / superficie.larghezza_pixel) * vista.w,
      sy: vista.y + (y / superficie.altezza_pixel) * vista.h,
    }),
    [vista, superficie.larghezza_pixel, superficie.altezza_pixel],
  );

  /**
   * Ingrandisce tenendo fermo il punto sotto il cursore.
   *
   * ⚠️ **Non attorno al centro: attorno al cursore.** Ingrandire dal centro allontana
   * proprio il connettore che si sta guardando, e costringe a inseguirlo scorrendo a
   * ogni scatto di rotella. Con il punto sotto il cursore che resta fermo, ci si
   * avvicina a ciò che si vuole senza mai perderlo di vista.
   */
  const ingrandisci = useCallback(
    (fattore: number, versoX?: number, versoY?: number) => {
      const el = contenitore.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const cx = versoX ?? r.left + r.width / 2;
      const cy = versoY ?? r.top + r.height / 2;
      setZoom((z) => {
        const nuovo = Math.min(12, Math.max(1, z * fattore));
        if (nuovo === z) return z;
        const k = nuovo / z;
        // il punto sotto il cursore resta dov'è: si compensa lo scarto introdotto
        // dall'ingrandimento attorno al centro del contenitore
        const dx = cx - r.left - r.width / 2;
        const dy = cy - r.top - r.height / 2;
        setScorrimento((s) => ({
          x: nuovo === 1 ? 0 : (s.x - dx) * k + dx,
          y: nuovo === 1 ? 0 : (s.y - dy) * k + dy,
        }));
        return nuovo;
      });
    },
    [],
  );

  /*
    ⚠️ I palloncini si dispongono lungo il BORDO, ordinati per posizione del punto.
    Metterli accanto al pezzo li farebbe sovrapporre appena due connettori sono vicini —
    ed è la norma su una morsettiera. Lungo il bordo non si toccano mai, e la linea di
    conduzione dice a quale punto appartiene ciascuno.
  */
  const richiami = useMemo(() => {
    /*
      ⚠️ Una parte compare UNA volta sola. Se per qualunque motivo arrivassero due segni
      con lo stesso `parte`, React userebbe la stessa chiave per due elementi e il
      disegno diventerebbe instabile — un cerchio che sparisce e riappare a ogni
      ridisegno, senza che niente segnali un errore. Vince il primo: gli altri sono
      residui.
    */
    const visti = new Set<string>();
    const ordinati = [...superficie.segni]
      .filter((g) => (visti.has(g.parte) ? false : (visti.add(g.parte), true)))
      .map((g, i) => ({ segno: g, indice: i }))
      .sort((a, b) => a.segno.y - b.segno.y || a.segno.x - b.segno.x);
    const perLato = Math.ceil(ordinati.length / 2) || 1;
    return ordinati.map((v, posizione) => {
      const aSinistra = posizione < perLato;
      const nelLato = aSinistra ? posizione : posizione - perLato;
      const quanti = aSinistra ? perLato : ordinati.length - perLato;
      const passo = riquadro.h / (quanti + 1);
      return {
        ...v,
        numero: posizione + 1,
        bx: aSinistra ? riquadro.x - 26 : riquadro.x + riquadro.w + 26,
        by: riquadro.y + passo * (nelLato + 1),
      };
    });
  }, [superficie.segni, riquadro]);

  const nomeDi = (id: string) => parti.find((p) => p.id === id);

  return (
    <Box
      ref={contenitore}
      onWheel={(e) => {
        e.preventDefault();
        /*
          ⚠️ **Il passo segue QUANTO si è girato, non quante volte.** Con un fattore
          fisso per evento un solo colpo di trackpad — che ne manda decine — moltiplicava
          venti volte di fila e arrivava al tetto in un attimo: da fuori sembra che lo
          zoom impazzisca da solo. La forma esponenziale sul delta rende il gesto
          proporzionale al movimento della mano, come in qualunque visore di immagini.

          ⚠️ `deltaMode` va normalizzato: 0 sono pixel, 1 sono righe (~16 px), 2 sono
          pagine. Un mouse a scatti manda righe, un trackpad manda pixel: senza questa
          conversione lo stesso codice si comporta in due modi opposti sui due dispositivi.
        */
        const unita = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
        const passo = Math.max(-120, Math.min(120, e.deltaY * unita));

        if (e.shiftKey && parteScelta) {
          const attuale = superficie.segni.find((g) => g.parte === parteScelta);
          if (attuale) {
            const minimo = Math.round(superficie.larghezza_pixel * 0.002);
            const massimo = Math.round(superficie.larghezza_pixel * 0.25);
            const nuovo = Math.min(
              massimo,
              Math.max(minimo, Math.round(attuale.r * Math.exp(-passo * 0.0012))),
            );
            if (nuovo !== attuale.r) onRaggio(parteScelta, nuovo);
            return;
          }
        }
        ingrandisci(Math.exp(-passo * 0.0015), e.clientX, e.clientY);
      }}
      onDoubleClick={(e) => {
        /*
          ⚠️ Il doppio clic ingrandisce SOLO quando non si sta posando. È il gesto
          naturale di chi ha il trackpad, ma da armati il primo dei due clic scriverebbe
          una coordinata: si otterrebbe un punto messo per sbaglio e uno zoom che
          nessuno collega alla causa. Con `alt` rimpicciolisce, come in ogni visore.
        */
        if (armato) return;
        e.preventDefault();
        ingrandisci(e.altKey ? 1 / 2 : 2, e.clientX, e.clientY);
      }}
      onMouseDown={(e) => {
        if (inTrascinamento) return;
        premuto.current = { x: e.clientX, y: e.clientY, scorrimento };
        haScorso.current = false;
      }}
      onMouseMove={(e) => {
        if (inTrascinamento) {
          const p = versoImmagine(e.clientX, e.clientY);
          if (p) onSposta(inTrascinamento, p.x, p.y);
          return;
        }
        const p = premuto.current;
        if (!p) return;
        const dx = e.clientX - p.x;
        const dy = e.clientY - p.y;
        /*
          ⚠️ Sotto i 4 pixel è un CLIC, sopra è uno scorrimento. Senza questa soglia
          ogni posa di un punto sposterebbe anche l'immagine di un pelo, e il punto
          finirebbe qualche pixel più in là di dove si è guardato — l'errore più
          difficile da attribuire, perché sembra imprecisione della mano.
        */
        if (!haScorso.current && Math.abs(dx) + Math.abs(dy) < 4) return;
        haScorso.current = true;
        setScorrimento({ x: p.scorrimento.x + dx, y: p.scorrimento.y + dy });
      }}
      onMouseUp={(e) => {
        setInTrascinamento(null);
        const scorso = haScorso.current;
        premuto.current = null;
        haScorso.current = false;
        // ⚠️ Si posa SOLO da armati. È l'intera ragione per cui `armato` esiste.
        if (scorso || !armato || !parteScelta) return;
        const p = versoImmagine(e.clientX, e.clientY);
        if (p) {
          onPoni(p.x, p.y);
          onPosata();
        }
      }}
      onMouseLeave={() => {
        setInTrascinamento(null);
        premuto.current = null;
        haScorso.current = false;
      }}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        /*
          ⚠️ **Un'altezza minima alta qui rompe la colonna che la contiene.** Con 420
          fissi la tavola si rifiutava di scendere sotto quella misura, il contenitore
          flex traboccava, e quello che stava sotto finiva NASCOSTO DIETRO la tavola —
          righe tagliate a metà e chevron che galleggiavano nel nero, senza che niente
          segnalasse un errore. In una colonna elastica il minimo lo decide chi
          distribuisce lo spazio, non chi lo occupa.
        */
        minHeight: 180,
        bgcolor: COLORI.tavolo,
        borderRadius: 1,
        overflow: 'hidden',
        cursor: armato ? 'crosshair' : zoom > 1 ? 'grab' : 'default',
        userSelect: 'none',
      }}
    >
      {superficie.immagine_url && (
        <Box
          component="img"
          src={superficie.immagine_url}
          alt={superficie.nome}
          draggable={false}
          onLoad={() => setCaricata(true)}
          sx={{
            position: 'absolute',
            left: vista.x,
            top: vista.y,
            width: vista.w,
            height: vista.h,
            objectFit: 'fill',
            imageRendering: zoom > 3 ? 'pixelated' : 'auto',
            opacity: caricata ? 1 : 0,
            transition: 'opacity .2s ease',
          }}
        />
      )}

      {/*
        ⚠️ **Lo stato si DICHIARA a schermo.** Un puntatore a croce è un segnale troppo
        debole per una modalità in cui un clic scrive un dato: chi guarda la fotografia
        non guarda il cursore. La fascia dice cosa succederà al prossimo clic, e sparisce
        appena non succede più niente.
      */}
      {armato && parteScelta && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 3,
            px: 1.5,
            py: 0.75,
            bgcolor: COLORI.attivo,
            color: '#14171C',
          }}
        >
          <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
            Tocca dov'è: {parti.find((p) => p.id === parteScelta)?.nome ?? parteScelta}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 11.5, opacity: 0.75 }}>
            Esc per annullare
          </Typography>
        </Stack>
      )}

      {!caricata && superficie.immagine_url && (
        <Stack
          alignItems="center"
          spacing={1.25}
          sx={{ position: 'absolute', inset: 0, justifyContent: 'center' }}
        >
          <CircularProgress size={26} sx={{ color: COLORI.tracciato }} />
          <Typography sx={{ color: 'rgba(232,234,237,.6)', fontSize: 12 }}>
            apro {superficie.nome.toLowerCase()}…
          </Typography>
        </Stack>
      )}

      <svg
        width="100%"
        height="100%"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          overflow: 'visible',
          // ⚠️ I richiami restano nascosti finché la fotografia non è a schermo:
          // disegnarli sul vuoto li fa comparire in posizioni che sembrano casuali.
          opacity: caricata ? 1 : 0,
        }}
      >
        {richiami.map(({ segno, bx, by }) => {
          const { sx, sy } = versoSchermo(segno.x, segno.y);
          const colore = coloreDi(segno.origine);
          const attivo = segno.parte === parteScelta;
          const bozza = segno.origine === 'bozza_ia';
          const raggio = Math.max(7, (segno.r / superficie.larghezza_pixel) * vista.w);
          return (
            <g key={segno.parte}>
              {/* linea di conduzione: dal palloncino al punto, sottile e senza frecce */}
              <line
                x1={bx}
                y1={by}
                x2={sx}
                y2={sy}
                stroke="rgba(0,0,0,.55)"
                strokeWidth={3}
              />
              <line
                x1={bx}
                y1={by}
                x2={sx}
                y2={sy}
                stroke={attivo ? COLORI.attivo : colore}
                strokeWidth={1.25}
                strokeDasharray={bozza ? '4 3' : undefined}
                opacity={0.9}
              />
              {/* il cerchio sul pezzo: aperto, per non coprire ciò che indica */}
              <circle
                cx={sx}
                cy={sy}
                r={raggio}
                fill="none"
                stroke="rgba(0,0,0,.55)"
                strokeWidth={4}
              />
              <circle
                cx={sx}
                cy={sy}
                r={raggio}
                fill="none"
                stroke={attivo ? COLORI.attivo : colore}
                strokeWidth={2}
                strokeDasharray={bozza ? '5 4' : undefined}
              />
              <circle cx={sx} cy={sy} r={2} fill={attivo ? COLORI.attivo : colore} />
            </g>
          );
        })}
      </svg>

      {richiami.map(({ segno, numero, bx, by }) => {
        const parte = nomeDi(segno.parte);
        const colore = coloreDi(segno.origine);
        const attivo = segno.parte === parteScelta;
        return (
          <Box
            key={`b-${segno.parte}`}
            onMouseDown={(e) => {
              e.stopPropagation();
              onScegli(segno.parte);
              setInTrascinamento(segno.parte);
            }}
            onClick={(e) => e.stopPropagation()}
            title="Trascina per spostare il punto"
            sx={{
              position: 'absolute',
              left: bx,
              top: by,
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              flexDirection: bx < riquadro.x ? 'row-reverse' : 'row',
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: attivo ? COLORI.attivo : colore,
                color: '#0B0E11',
                fontSize: 11,
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                boxShadow: '0 0 0 2px rgba(0,0,0,.5)',
                flexShrink: 0,
              }}
            >
              {numero}
            </Box>
            <Typography
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 11,
                letterSpacing: '.04em',
                color: attivo ? COLORI.attivo : COLORI.tracciato,
                bgcolor: 'rgba(10,13,17,.82)',
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                whiteSpace: 'nowrap',
              }}
            >
              {parte?.sigla?.trim() || parte?.nome || segno.parte}
            </Typography>
          </Box>
        );
      })}

      {/*
        ⚠️ I comandi ci sono anche se la rotella basterebbe: un gesto che nessuno
        annuncia è un gesto che nessuno prova. La percentuale dice a colpo d'occhio se
        si sta guardando l'insieme o un dettaglio — utile quando si torna sulla
        schermata dopo aver fatto altro.
      */}
      {/*
        ⚠️ **La taglia del cerchio è un comando, non solo un gesto.** Serve davvero:
        tre spie a un centimetro l'una dall'altra vogliono un cerchio minuscolo, una
        chiavetta di comunicazione ne vuole uno grande. Un cerchio unico per tutti
        mente su entrambi — e il gesto da solo (maiuscolo+rotella) non lo scopre nessuno.
      */}
      {parteScelta && superficie.segni.some((g) => g.parte === parteScelta) && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            left: 10,
            bottom: 10,
            bgcolor: 'rgba(10,13,17,.85)',
            borderRadius: 1,
            px: 0.75,
            py: 0.25,
          }}
        >
          <Typography sx={{ fontSize: 11, color: 'rgba(232,234,237,.65)', mr: 0.25 }}>
            cerchio
          </Typography>
          <Comando
            titolo="Cerchio più piccolo"
            onClick={() => {
              const g = superficie.segni.find((x) => x.parte === parteScelta);
              if (g) {
                onRaggio(
                  parteScelta,
                  Math.max(Math.round(superficie.larghezza_pixel * 0.002), Math.round(g.r / 1.3)),
                );
              }
            }}
          >
            −
          </Comando>
          <Box
            sx={{
              px: 0.5,
              fontSize: 11,
              fontVariantNumeric: 'tabular-nums',
              color: COLORI.tracciato,
              minWidth: 46,
              textAlign: 'center',
              userSelect: 'none',
            }}
          >
            {(() => {
              const g = superficie.segni.find((x) => x.parte === parteScelta);
              if (!g) return '—';
              // in millimetri veri: «43 px» non dice niente, «15 mm» sì
              const mmPerPx = (superficie.larghezza_metri * 1000) / superficie.larghezza_pixel;
              return `${Math.round(g.r * 2 * mmPerPx)} mm`;
            })()}
          </Box>
          <Comando
            titolo="Cerchio più grande"
            onClick={() => {
              const g = superficie.segni.find((x) => x.parte === parteScelta);
              if (g) {
                onRaggio(
                  parteScelta,
                  Math.min(Math.round(superficie.larghezza_pixel * 0.25), Math.round(g.r * 1.3)),
                );
              }
            }}
          >
            +
          </Comando>
        </Stack>
      )}

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        sx={{
          position: 'absolute',
          right: 10,
          bottom: 10,
          bgcolor: 'rgba(10,13,17,.85)',
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
        }}
      >
        <Comando titolo="Rimpicciolisci" onClick={() => ingrandisci(1 / 1.4)}>
          −
        </Comando>
        <Box
          onClick={() => {
            setZoom(1);
            setScorrimento({ x: 0, y: 0 });
          }}
          title="Torna a vedere tutto"
          sx={{
            px: 0.75,
            fontSize: 11,
            fontVariantNumeric: 'tabular-nums',
            color: COLORI.tracciato,
            cursor: 'pointer',
            minWidth: 40,
            textAlign: 'center',
            userSelect: 'none',
          }}
        >
          {Math.round(zoom * 100)}%
        </Box>
        <Comando titolo="Ingrandisci" onClick={() => ingrandisci(1.4)}>
          +
        </Comando>
      </Stack>

      {zoom === 1 && superficie.immagine_url && (
        <Typography
          sx={{
            position: 'absolute',
            left: 10,
            bottom: 12,
            fontSize: 11,
            color: 'rgba(232,234,237,.55)',
            pointerEvents: 'none',
          }}
        >
          rotella per ingrandire · trascina per spostare
        </Typography>
      )}

      {!superficie.immagine_url && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          {/*
            ⚠️ **Non dire «il file non c'è» quando quasi sempre è un permesso negato.**
            L'URL si risolve dal client e il rifiuto viene inghiottito dal `.catch`:
            attribuire la colpa a un file mancante manda a cercare nel posto sbagliato,
            e il file di solito è lì. Si dichiarano tutte e due le cause possibili,
            nell'ordine in cui conviene controllarle.
          */}
          <Typography
            sx={{ color: COLORI.tracciato, fontSize: 13, maxWidth: 380, textAlign: 'center', px: 2 }}
          >
            La fotografia non si è aperta. Di solito è un permesso: le regole di Storage
            per <code>ar_apparecchi/</code> devono essere pubblicate, e il tuo utente
            deve avere l'accesso. Se le regole ci sono, allora il file non è più nel
            bucket.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/** Un tasto dei comandi di ingrandimento. Nient'altro: bersaglio comodo, niente decoro. */
function Comando({
  titolo,
  onClick,
  children,
}: {
  titolo: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Box
      onClick={onClick}
      title={titolo}
      sx={{
        width: 24,
        height: 24,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 0.75,
        color: COLORI.tracciato,
        fontSize: 15,
        lineHeight: 1,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { bgcolor: 'rgba(255,255,255,.12)' },
      }}
    >
      {children}
    </Box>
  );
}
